import { createStore } from '@/lib/tinyStore';
import type { NotificationItem, NotificationsState } from './types';

/**
 * The notification centre keeps a bounded window in memory. Older rows live in
 * Postgres and are fetched with keyset pagination — never hold 90 days of
 * activity in a tab.
 */
const MAX_ITEMS = 300;

export const notificationStore = createStore<NotificationsState>({
  items: [],
  unseenCount: 0,
  unreadCount: 0,
  loading: true,
  failed: false,
});

function recount(items: readonly NotificationItem[]): Pick<NotificationsState, 'unseenCount' | 'unreadCount'> {
  let unseenCount = 0;
  let unreadCount = 0;
  for (const item of items) {
    if (!item.isSeen) unseenCount += 1;
    if (!item.isRead) unreadCount += 1;
  }
  return { unseenCount, unreadCount };
}

export const notificationActions = {
  /** Replace the window (initial load / refetch). Newest first. */
  hydrate(items: readonly NotificationItem[]): void {
    const next = items.slice(0, MAX_ITEMS);
    notificationStore.set({ items: next, loading: false, failed: false, ...recount(next) });
  },

  /**
   * The feed could not be read. Distinct from "empty" on purpose: an empty
   * Activity list reads as "you're all caught up", which is a lie when the
   * request failed — the user must be told to retry instead.
   */
  fail(): void {
    notificationStore.set((state) => ({ ...state, loading: false, failed: true }));
  },

  /**
   * Insert one row, coalescing on `groupKey` exactly like the partial unique
   * index `uq_notifications_group` does server-side.
   */
  ingest(item: NotificationItem): NotificationItem {
    const state = notificationStore.get();
    const groupIndex = item.groupKey
      ? state.items.findIndex((row) => row.groupKey === item.groupKey && !row.isRead)
      : -1;

    let items: NotificationItem[];
    let stored = item;

    if (groupIndex >= 0) {
      const existing = state.items[groupIndex];
      stored = {
        ...item,
        groupCount: (existing?.groupCount ?? 1) + 1,
        id: existing?.id ?? item.id,
      };
      items = [stored, ...state.items.slice(0, groupIndex), ...state.items.slice(groupIndex + 1)];
    } else {
      /* ID IS A SECOND IDENTITY, AND IT WAS NOT BEING HONOURED. A row can
         arrive twice with the same id and a DIFFERENT groupKey — a durable
         `notification.push` replayed after a reconnect is the ordinary case —
         and the groupKey match above cannot see that, so the same
         notification appeared twice in the bell and twice in the count. */
      items = [item, ...state.items.filter((row) => row.id !== item.id)];
    }

    if (items.length > MAX_ITEMS) items.length = MAX_ITEMS;
    notificationStore.set({ ...state, items, ...recount(items) });
    return stored;
  },

  markRead(id: string): void {
    notificationStore.set((state) => {
      const items = state.items.map((row) =>
        row.id === id && (!row.isRead || !row.isSeen) ? { ...row, isRead: true, isSeen: true } : row,
      );
      return { ...state, items, ...recount(items) };
    });
  },

  markAllSeen(): void {
    notificationStore.set((state) => {
      if (state.unseenCount === 0) return state;
      const items = state.items.map((row) => (row.isSeen ? row : { ...row, isSeen: true }));
      return { ...state, items, ...recount(items) };
    });
  },

  markAllRead(): void {
    notificationStore.set((state) => {
      if (state.unreadCount === 0) return state;
      const items = state.items.map((row) =>
        row.isRead && row.isSeen ? row : { ...row, isRead: true, isSeen: true },
      );
      return { ...state, items, ...recount(items) };
    });
  },

  dismiss(id: string): void {
    notificationStore.set((state) => {
      const items = state.items.filter((row) => row.id !== id);
      if (items.length === state.items.length) return state;
      return { ...state, items, ...recount(items) };
    });
  },

  /**
   * WITHDRAWN BY THE SERVER, not read by the user.
   *
   * A `call_incoming` row stops being true the moment the call is answered,
   * declined or missed. Until this existed nothing could say so: the only
   * signal at those moments was `call.cancelled`, which dismisses a popup and
   * cannot touch the Activity feed, so every ring stayed in the bell and in the
   * badge for ever.
   *
   * BY KEY AS WELL AS BY ID, because the two disagree in the case that matters:
   * `ingest` collapses onto a groupKey and KEEPS THE FIRST ROW'S ID, so the id
   * this client holds is not necessarily an id the server ever names. The key
   * is the stable handle.
   */
  withdraw(ids: readonly string[], groupKey: string | null): void {
    notificationStore.set((state) => {
      const doomed = new Set(ids);
      const items = state.items.filter(
        (row) => !doomed.has(row.id) && !(groupKey !== null && row.groupKey === groupKey),
      );
      if (items.length === state.items.length) return state;
      return { ...state, items, ...recount(items) };
    });
  },
};

export const selectItems = (state: NotificationsState): readonly NotificationItem[] => state.items;
export const selectUnseen = (state: NotificationsState): number => state.unseenCount;
export const selectUnread = (state: NotificationsState): number => state.unreadCount;
export const selectLoading = (state: NotificationsState): boolean => state.loading;
export const selectFailed = (state: NotificationsState): boolean => state.failed;
